import React, { useEffect, useState } from "react";
import { useFlow } from "../../contexts/flow";
import serviceMethods from "../../service";
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { useFlowManager } from "../../contexts/flowManager";
import { Link } from 'react-router-dom';
import { orderFlowStages } from "../../utils"
import BreadcrumbWrapper from "../BreadcrumbWrapper"

const FlowManager = () => {
  const { flowDetails, setFlowDetails, allFlows, setAllFlows } = useFlow();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setSelectedResponse, setCustomFormId, setSelectedFlowStageId, setPrevStageResult, setFormData, setSelectedSubscription, setSelectedPurchase } = useFlowManager();
  const [flowResults, setFlowResults] = useState({})

  const { getFullFlowById, getAllFlows } = serviceMethods;

  const fetchFlows = async () => {
    try {
      const flowsRes = await getAllFlows()
      const flows = Array.isArray(flowsRes) ? flowsRes : [];
      setAllFlows(flows);
    } catch {
      console.error('Unable to fetch flows')
    }
  }

  const getMatchingFlow = async (id = flowDetails.id) => {
    if (id) {
      const matchingFlow = await getFullFlowById(id)
      if (!matchingFlow.ok) {
        // display flow not found message
        setSearchParams({})
        return
      }
      const data = await matchingFlow.json();
      setFlowDetails(data);
    }
  }

  const handleReview = async (e, stage, stageResult, flowInstanceId) => {
    const prevStage = flowDetails?.flowStages.find((prevStage) => prevStage.nextStageId === stage.flowStageId)

    const prevStageResult = flowResults[flowInstanceId].find((result) => result?.flowStageId === prevStage?.flowStageId)
    setPrevStageResult(prevStageResult)

    let response = {}
    if (stageResult) {
      for (const form of stage.forms) {
        const foundResponse = form.responses.find((response) => stageResult?.responseDataBindings?.includes(response.responseId))
        if (foundResponse) {
          response = foundResponse
          break
        }
      }
      if (stage.flowStageTypeId === '4b360e17-54ba-459e-8477-326bbd7a73db') { // if type is application review
        const customFormResponseId = stageResult.responseDataBindings[stageResult.responseDataBindings.length - 1]
        const tempRes = await serviceMethods.getCustomFormResponse(customFormResponseId)
        const data = await tempRes.json()
        const tempFormData = data.answers.answersJson.reduce((answerObj, currentAnswer) => {
          answerObj[Object.keys(currentAnswer)[0]] = currentAnswer[Object.keys(currentAnswer)[0]]
          return answerObj
        }, {})
        setFormData(tempFormData)
      }
      if (stage.flowStageTypeId === '4f5b1de3-8e8a-4f93-9bb0-c47c3a9d02f2') { // if type is registration complete
        const subscriptionId = stageResult.responseDataBindings[0]
        const purchaseId = stageResult.responseDataBindings[1]
        const subscription = await serviceMethods.getSubscriptionById(subscriptionId)

        const purchaseRes = await serviceMethods.getPurchaseById(purchaseId)
        const purchase = await purchaseRes.json()

        setSelectedSubscription(subscription)
        setSelectedPurchase(purchase)
      }
    } else {
      for (const form of prevStage.forms) {
        const foundResponse = form.responses.find((response) => prevStageResult?.responseDataBindings?.includes(response.responseId))
        if (foundResponse) {
          response = foundResponse
          break
        }
      }
    }

    setSelectedResponse(response)
    setCustomFormId(stage.dependencyIdentifiers[1])
    setSelectedFlowStageId(stage.flowStageId)
  }

  useEffect(()=>{
    fetchFlows();
  },[])

  useEffect(()=>{
    if (searchParams.get('flow-id')){
      getMatchingFlow(searchParams.get('flow-id'))
    }
  },[searchParams])

  useEffect(()=>{
    if (flowDetails.flowId) {
      const resultsObj = {}
      const stages = orderFlowStages(flowDetails?.flowStages)
      for (const stage of stages){
        for (const stageResult of stage.flowStageResults) {
          if (resultsObj[stageResult.flowInstanceId] && Array.isArray(resultsObj[stageResult.flowInstanceId])) {
            resultsObj[stageResult.flowInstanceId].push(stageResult)
          } else {
            resultsObj[stageResult.flowInstanceId] = [stageResult]
          }
        }
      }
      setFlowResults(resultsObj)
    } else {
      setFlowResults({})
    }
  },[flowDetails])

  return (
    <div>
      <BreadcrumbWrapper crumbArray={[{path: '/flow-manager', label: 'Flow Manager'}]}/>
      <div className='w-full flex flex-col items-center p-8 gap-4'>
        <div className='flex w-full justify-center'>
          <label className='flex flex-col'>
            <select
              value={flowDetails?.flowId || ''}
              onChange={(e) => {
                if (e.target.value) {
                  getMatchingFlow(e.target.value)

                  setSearchParams({ 'flow-id': e.target.value })
                } else {
                  setFlowDetails({
                    id: null,
                    flowTitle: null
                  })
                  setSearchParams({})
                }
              }}
            >
              <option value=''>
                Select a Flow
              </option>
              {allFlows.map((flow) => (
                <option value={flow.id} key={flow.id}>
                  {flow.flowTitle}
                </option>
              ))}
            </select>
          </label>
        </div>

        {flowDetails.flowId &&
          <div className='flex flex-col w-fit items-center gap-4 border rounded px-4 py-3 bg-gray-400'>
            <div className='flex w-full justify-center gap-4'>
              {flowDetails?.flowStages && <div className='flex flex-col border rounded px-2 py-1 bg-white w-96'>
                <h2>Name: {flowDetails.flowTitle}</h2>
              </div>}
              {orderFlowStages(flowDetails?.flowStages).map((stage) => {
                return (
                  <div key={`stage-${stage.flowStageTitle}`} className='flex flex-col border rounded px-2 py-1 bg-white w-96'>
                    <h2>Stage: {stage.flowStageTitle}</h2>
                    <div className='flex gap-4'>
                      {stage?.forms?.map((form) => {
                        return (
                          <div key={`form-${form.formId}`} className='flex flex-col border rounded px-2 py-1'>
                            <h2>{form.formTitle}</h2>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>

            {Object.values(flowResults).sort((a, b) => new Date(a[a.length - 1].createdAt) - new Date(b[b.length - 1].createdAt)).map((results, i) => {
              return (
                <div key={`flowInstanceId-${results[0].flowInstanceId}-${i}`} className='flex flex border rounded px-2 py-1 bg-white w-fit gap-4'>
                  <div className='flex flex-col w-96'>
                    <h2>{i + 1}. Flow Instance ID</h2>
                    <h2>{results[0].flowInstanceId}</h2>
                  </div>
                  {orderFlowStages(flowDetails?.flowStages).map((stage) => {
                    // sorts / finds the most recently submitted flowStageResult
                    const stageResult = results.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)).findLast((result) => result.flowStageId === stage.flowStageId)
                    return (
                      <button
                        key={`instanceStage-${results[0].flowInstanceId}${stage.flowStageId}`}
                        className='flex flex-col items-start border rounded px-2 py-1 bg-gray-100 w-96'
                        onClick={(e) => {
                          handleReview(e, stage, stageResult, results[0].flowInstanceId)
                        }}
                      >
                        <Link to="/review" className='flex flex-col items-start w-full'>
                          <h2>{stage.flowStageTitle}</h2>
                          <h2>{stageResult ? stageResult.id : 'N/A'}</h2>
                        </Link>
                      </button>
                    )
                  })}
                </div>
              )
            })}
          </div>
        }
      </div>
    </div>
  )
}

export default FlowManager;